import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resolveModulesForUser } from '../utils/roles';
import { Loader2, ShieldAlert } from 'lucide-react';

// Role-based route permissions mapping
// Maps route paths to required permission keys
const routePermissions = {
  // Management
  '/erp/superadmin': ['superadmin'],
  '/erp/superadmin/users': ['superadmin_users'],
  '/erp/superadmin/audit': ['superadmin_audit'],
  '/erp/superadmin/daily': ['superadmin_daily'],
  '/erp/superadmin/mis': ['superadmin_mis'],
  '/erp/superadmin/permissions': ['superadmin_permissions'],
  '/erp/superadmin/credentials': ['superadmin_credentials'],
  '/erp/admin': ['admin'],
  '/erp/dashboard': ['admin'],
  '/erp/inquiries': ['inquiries'],
  '/erp/floor-manager': ['floormanager'],
  
  // Sales & CRM
  '/erp/orders': ['orders'],
  '/erp/sales-invoice': ['salesinvoice'],
  '/erp/purchase-invoice': ['purchasemodule'],
  '/erp/quotations': ['quotations'],
  '/erp/discount-approvals': ['discountapprovals'],
  '/erp/leads': ['leads'],
  '/erp/abandoned-carts': ['abandonedcarts'],
  '/erp/sales-tasks': ['salestasks'],
  '/erp/sales-targets': ['salestargets'],
  '/erp/incentives': ['incentives'],
  '/erp/performance': ['performance'],
  '/erp/sfa': ['sfa'],
  '/erp/crm': ['crm'],
  '/erp/customer-master': ['customermaster'],
  '/erp/rewards': ['rewards'],
  
  // Operations
  '/erp/production': ['production'],
  '/erp/job-cards': ['jobcards'],
  '/erp/operator': ['operator'],
  '/erp/inventory': ['inventory'],
  '/erp/purchase': ['purchase'],
  '/erp/transport': ['transport'],
  '/erp/driver-dashboard': ['driverdashboard'],
  '/erp/job-work': ['jobwork'],
  
  // HR & Admin
  '/erp/hr': ['hr'],
  '/erp/onboarding': ['onboarding'],
  '/erp/hr-letters': ['hrletters'],
  '/erp/leave': ['leave'],
  '/erp/holidays': ['holidays'],
  '/erp/assets': ['assets'],
  
  // Finance
  '/erp/accounts': ['accounts'],
  '/erp/payments': ['payments'],
  '/erp/ledger': ['ledger'],
  '/erp/payouts': ['payouts'],
  '/erp/expenses': ['expenses'],
  '/erp/gst': ['gst'],
  '/erp/refunds': ['refunds'],
  '/erp/vendor-management': ['vendors'],
  '/erp/wallet': ['wallet'],
  
  // Settings & Tools
  '/erp/ai-forecast': ['aiforecast'],
  '/erp/forecast': ['forecast'],
  '/erp/cms': ['cms'],
  '/erp/branches': ['branches'],
  '/erp/product-config': ['productconfig'],
  '/erp/glass-weight': ['glassweight'],
  '/erp/tally-settings': ['tally'],
  '/erp/pincode-settings': ['pincode'],
  '/erp/settings': ['settings'],
  '/erp/activity-log': ['activitylog'],
  '/erp/automation': ['automation'],
  '/erp/activity-reports': ['activityreports'],
  
  // Additional
  '/erp/sales-hub': ['crm', 'sfa'],
};

// Role permissions - shared single source of truth (src/utils/roles.js).
// Any account role, including legacy aliases, resolves to its module list here.

// Check if user has permission for a route
const hasRoutePermission = (user, routePath) => {
  const userPermissions = resolveModulesForUser(user);
  const requiredPermissions = routePermissions[routePath];
  
  // If no specific permissions required for this route, allow access
  if (!requiredPermissions) return true;
  
  // Check if user has any of the required permissions
  return requiredPermissions.some(perm => userPermissions.includes(perm));
};

// Access Denied Component
const AccessDenied = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center p-8 max-w-md">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <ShieldAlert className="w-10 h-10 text-red-600" />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
      <p className="text-slate-600 mb-6">
        आपके पास इस पेज को access करने की permission नहीं है। 
        अगर आपको लगता है कि यह गलती से हुआ है, तो अपने admin से संपर्क करें।
      </p>
      <button
        onClick={() => window.history.back()}
        className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
      >
        Go Back
      </button>
    </div>
  </div>
);

// Protected Route Component
export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Show loading while auth is being checked
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-teal-600 mx-auto mb-3" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Check route permission
  const currentPath = location.pathname;
  
  if (!hasRoutePermission(user, currentPath)) {
    return <AccessDenied />;
  }
  
  return children;
};

// Export helper function for use in other components
export { hasRoutePermission, routePermissions };
